import { Observable } from 'rxjs';
import { AlertsApi, Contact, FindAlertRequest, PagedAlertFormsResponse, PagedAlertSettingsResponse, PagedReceivedAlertResponse, PagedRecipientResponse, Recipient, SingleAlertDefaultsRequest, SingleAlertDefaultsResponse, SingleAlertSettingsResponse, AlertStatus, AlertType, PriorityType } from 'src/app/hickory-api';
import { mockHandler } from '../mock.handler';
import { alertSettingsData } from './data/AlertSettingsData';
import { contactData } from './data/ContactData';
import { namedIncidentData } from './data/NamedIncidentData';
import { getPage } from './data/PageHelper';
import { getSingle } from './data/SingleHelper';

export class MockAlertsApi extends AlertsApi {
  private alertsDefault: SingleAlertDefaultsResponse = {
    item: {
      fields: [
        {
          name: 'priority',
          required: true
        },
        {
          name: 'template',
          required: false
        },
        {
          name: 'incident',
          required: false
        },
        {
          name: 'location',
          required: false
        },
        {
          name: 'recipients',
          required: true
        },
        {
          name: 'subject',
          required: true
        },
        {
          name: 'message',
          required: true
        }
      ],
      sender: {
        displayName: 'LastName, FirstName'
      },
      recipients: {
        groupIds: Array<number>() // Default groupIds imply MAGs (Mandatory Groups)
      }
    }
  };
  private alertsDefaultReply: SingleAlertDefaultsResponse = {
    item: {
      message: {
        priority: PriorityType.NonEmergency,
        basic: {
          subject: 'Re: AlertSense Dev Environment',
          message: '',
          messageRichContent:
            '<p class="forward-header"><strong>Original Message from User, Other</strong>' +
            '<br /> on 11/1/2017 at 11:55:30 AM</p>\r\n<blockquote>test</blockquote>'
        },
        language: 'en-us'
      },
      fields: [
        {
          name: 'priority',
          required: true
        },
        {
          name: 'template',
          required: false
        },
        {
          name: 'location',
          required: false
        },
        {
          name: 'recipients',
          required: true
        },
        {
          name: 'subject',
          required: true
        },
        {
          name: 'message',
          required: true
        }
      ],
      sender: {
        displayName: 'LastName, FirstName'
      },
      recipients: {
        groupIds: [4424],
        contactIds: [4578, 4588]
      }
    }
  };
  private alertsDefaultIncidentMags: SingleAlertDefaultsResponse = {
    item: {
      fields: [
        {
          name: 'priority',
          required: true
        },
        {
          name: 'template',
          required: false
        },
        {
          name: 'incident',
          required: false
        },
        {
          name: 'location',
          required: false
        },
        {
          name: 'recipients',
          required: true
        },
        {
          name: 'subject',
          required: true
        },
        {
          name: 'message',
          required: true
        }
      ],
      sender: {
        displayName: 'LastName, FirstName'
      },
      recipients: {
        mandatoryGroupIds: [4424]
      }
    }
  };

  getAlertsDefault(request?: SingleAlertDefaultsRequest): Observable<any> {
    if (request.relatedAlertId) {
      return mockHandler(this.alertsDefaultReply);
    } else if (request.incidentId === namedIncidentData.predefined[0].id) {
      return mockHandler(this.alertsDefaultIncidentMags);
    } else {
      return mockHandler(this.alertsDefault);
    }
  }

  getAlertsByIdRecipients(
    id: number,
    recipientIds?: Array<string>,
    isMandatory?: boolean,
    pollOptionId?: number,
    name?: string,
    email?: string,
    page?: number,
    pageSize?: number,
    sort?: string
  ): Observable<any> {
    const alertSettings = alertSettingsData.all.find(s => s.id === id);
    let contacts: Contact[] = [];
    if (alertSettings.recipients.contactIds) {
      contacts = contactData.all.filter(c => alertSettings.recipients.contactIds.indexOf(c.id) >= 0);
    }
    const pagedRecipients: PagedRecipientResponse = getPage(contacts, page, pageSize);
    return mockHandler(pagedRecipients);
  }

  getAlerts(hidden?: boolean): Observable<any> {
    // Page info isn't in the swagger, so we always get the default page
    const pagedAlertSettings: PagedAlertSettingsResponse = getPage(alertSettingsData.all, 1, 25);
    return mockHandler(pagedAlertSettings);
  }

  findAlerts(body?: FindAlertRequest): Observable<any> {
    // Page info isn't in the swagger, so we always get the default page
    const pagedAlertSettings: PagedAlertSettingsResponse = getPage(alertSettingsData.all, 1, 25);
    return mockHandler(pagedAlertSettings);
  }

  getAlertsById(id: number): Observable<any> {
    const alertSettings = alertSettingsData.all.find(s => s.id === id);
    const settings: SingleAlertSettingsResponse = getSingle(alertSettings);
    return mockHandler(settings);
  }

  getAlertsByIdStatus(id: number): Observable<any> {
    const status: AlertStatus = {
      id: id,
      status: 'sent',
      alertCompletion: AlertStatus.AlertCompletionEnum.Sent
    }
    return mockHandler(getSingle(status));
  }

  getAlertsSent(
    after?: Date,
    before?: Date,
    alertType?: string,
    facilityName?: string,
    hidden?: boolean,
    incident?: string,
    messagepriority?: string,
    alertStatuses?: Array<string>,
    page?: number,
    pageSize?: number,
    sort?: string,
    subject?: string,
  ): Observable<any> {
    const pagedAlertSettings: PagedAlertSettingsResponse = getPage(alertSettingsData.all, page, pageSize);
    return mockHandler(pagedAlertSettings);
  }

  getAlertsReceived(
    after?: Date,
    before?: Date,
    alertType?: string,
    facilityName?: string,
    hidden?: boolean,
    incident?: string,
    messagepriority?: string,
    unread?: boolean,
    page?: number,
    pageSize?: number,
    sort?: string,
    subject?: string
  ): Observable<any> {
    const pagedAlertSettings: PagedReceivedAlertResponse = getPage(alertSettingsData.all, page, pageSize);
    return mockHandler(pagedAlertSettings);
  }

  getAlertReceivedResults(id: number): Observable<any> {
    const recipient: Recipient = {};
    return mockHandler(getSingle(recipient));
  }

  getAlertsByIdResults(id: number): Observable<any> {
    return mockHandler(getSingle({})); // TODO
  }

  public getAlertForms(alertType?: string, ids?: Array<string>, name?: string): Observable<any> {
    const response: PagedAlertFormsResponse = {
      items: [
        {
          id: '5d8cee8e-6043-42a8-8a34-ac9024f62415',
          name: 'Default Alert Form',
          fields: [],
          alertType: AlertType.Default
        },
        {
          id: '778cee8e-6043-42a8-8a34-ac9024f62477',
          name: 'Default Poll Form',
          fields: [],
          alertType: AlertType.Poll
        }
      ]
    };
    return mockHandler(response);
  }

  public getAlertFormById(id: string): Observable<any> {
    return mockHandler({});
  }
}
